import { BlogAddComment } from "@/pages/blog/components/BlogAddComment.tsx";
import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { Reactions } from "@/components/reactions/Reactions.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useIntersectionObserver } from "@/hooks/use-intersection-observer.hook.tsx";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";
import {
  useBlogDeleteComment,
  useBlogEditComment,
  useBlogReadCommentNotifications,
  useBlogReadComments,
  useBlogSaveReactionToComment,
} from "@/queries/blog.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { ApiActionPayload } from "@/types/action.types.ts";
import { BlogPostModeType } from "@/types/blog.types.ts";
import { ApiComment } from "@/types/comment.types.ts";
import { BlogPostAttachments } from "@/pages/blog/components/BlogPostAttachments.tsx";
import {
  Box,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  HtmlViewer,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export interface BlogPostCommentProps {
  comment: ApiComment;
  postId?: number;
  mode?: BlogPostModeType;
}

export function BlogPostComment({ comment, postId, mode }: BlogPostCommentProps) {
  const { renderRelativeDate } = useFormatter();
  const [editIsOn, setEditIsOn] = useState<boolean>(false);
  const { t } = useTranslation();
  const { mutateAsync: deleteComment } = useBlogDeleteComment();
  const { mutateAsync: editComment } = useBlogEditComment();
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.5,
    rootMargin: "0px",
  });
  const { mutate: markAsRead } = useBlogReadComments();
  const { mutate: markNotificationAsRead } = useBlogReadCommentNotifications();
  const { mutateAsync: saveReaction } = useBlogSaveReactionToComment();
  const { replaceMentions } = useMentionsHook();
  const isEdited = comment.datetime_created_at.getTime() != comment.datetime_updated_at.getTime();

  const handleEditComment = () => {
    setEditIsOn(true);
  };

  const handleDeleteComment = async () => {
    if (!postId) return;

    await deleteComment({ postId, commentId: comment.id });

    analyticsService.trackEvent(analyticEvents.blog.commentDeleted);
  };

  useEffect(() => {
    if (isIntersecting && !comment.is_read && postId) {
      markAsRead({ postId, commentId: comment.id });
    }

    if (isIntersecting && comment.notification) {
      markNotificationAsRead({ notificationId: comment.notification.id, commentId: comment.id });
    }
  }, [isIntersecting, comment.is_read, comment.id, postId]);

  const save = useCallback(
    async (emoji: string, isNew: boolean) => {
      const { result } = await saveReaction({ commentId: comment.id, emoji, isNew, postId });

      analyticsService.trackEvent(
        isNew ? analyticEvents.blog.reactionAdded : analyticEvents.blog.reactionDeleted
      );

      return result.success;
    },
    [comment, postId]
  );

  const addComment = async (payload: ApiActionPayload) => {
    if (!postId) return false;

    const result = await editComment({
      postId,
      commentId: comment.id,
      text: payload.comment ?? "",
      fileUuids: payload.file_uuids,
    });

    analyticsService.trackEvent(analyticEvents.blog.commentCreated);

    return !!result.comment;
  };

  return (
    <Stack items={"start"} gap={"sm"} className={"group"} ref={elementRef}>
      <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
        <EmployeeItem
          employee={comment.author}
          size={"sm"}
          direction={"horizontal"}
          variant={"secondary"}
          showPosition={false}
          caption={renderRelativeDate(comment.datetime_created_at)}
          enablePreview
          enableLink
        />
        {isEdited && (
          <Tooltip delay={false}>
            <TooltipTrigger>
              <Text className={"text-secondary text-sm"}>({t("common:edited").toLowerCase()})</Text>
            </TooltipTrigger>
            <TooltipContent className={"max-w-40"}>
              {renderRelativeDate(comment.datetime_updated_at)}
            </TooltipContent>
          </Tooltip>
        )}
        {!comment.is_read && <div className={"h-2 w-2 bg-new rounded-full"}></div>}
        {!comment.reactions?.length && mode == "long" && (
          <EmojiPickerDropdown
            onEmojiSelect={async (emoji) => {
              await save(emoji, true);
            }}
          >
            <FontAwesomeIcon
              icon={"fa-light fa-face-smile-plus group-hover:opacity-100 opacity-0 duration-100"}
              className={"cursor-pointer"}
            />
          </EmojiPickerDropdown>
        )}
        {((comment.acl?.can_edit && !editIsOn) || comment.acl?.can_delete) && mode == "long" && (
          <DropdownMenu
            className={"group-hover:opacity-100 opacity-0 transition duration-200"}
            trigger={<FontAwesomeIcon icon={"fa-light fa-ellipsis"} className={"cursor-pointer"} />}
          >
            {comment.acl?.can_edit && !editIsOn && (
              <DropdownMenuItem
                onClick={() => handleEditComment()}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen-to-square"} />}
                title={t("common:edit")}
              />
            )}
            {comment.acl?.can_delete && (
              <DropdownMenuItem
                onClick={() => handleDeleteComment()}
                leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                variant={"menuDanger"}
                title={t("common:delete")}
              />
            )}
          </DropdownMenu>
        )}
      </Stack>

      {editIsOn ? (
        <Stack className={"w-full"}>
          <BlogAddComment
            postId={postId ?? 0}
            save={addComment}
            label={`${t("common:add_comment")}...`}
            initValue={replaceMentions(comment.text, comment?.mentioned_employees)}
            initialFiles={comment.files}
            onSaved={() => setEditIsOn(false)}
            onCancel={() => setEditIsOn(false)}
          />
        </Stack>
      ) : (
        <>
          <Box className={"break-word"}>
            <HtmlViewer content={replaceMentions(comment.text, comment?.mentioned_employees)} />
          </Box>
          {mode === "long" && comment.files.length > 0 && postId && (
            <BlogPostAttachments postId={postId} files={comment.files} showTitle={false} />
          )}
          {comment.reactions && comment.reactions.length > 0 && mode == "long" && (
            <Reactions reactions={comment.reactions} save={save} />
          )}
        </>
      )}
    </Stack>
  );
}
